import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { useState } from "react";
import { toast } from "sonner";
import {
  ChevronLeft,
  MapPin,
  Camera as CameraIcon,
  Bell,
  Activity,
  Cloud,
  Wifi,
  Battery,
  Copy,
  Smartphone,
  Radio,
  HeartPulse,
  Zap,
  AlertTriangle,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  RoavaCard,
  StatusChip,
  SecondaryButton,
  type StatusChipVariant,
} from "@/components/roava";
import { PageHeader } from "@/components/admin/PageHeader";
import { TravelerColorPicker } from "@/components/admin/TravelerColorPicker";
import { getMyTrip, getTripMembers, updateTripMemberColor } from "@/lib/trip.functions";
import {
  evaluateReadiness,
  readinessChipVariant,
} from "@/lib/admin/traveler-readiness";
import { getTripDiagnostics, getTravelerStatus } from "@/lib/admin.functions";
import { listInviteCodes } from "@/lib/invites.functions";
import { colorForUser, nameForColor } from "@/lib/traveler-colors";

export const Route = createFileRoute("/_authenticated/admin/travelers/$userId")({
  head: () =>
    pageMeta({
      title: "Traveler Status — Roava",
      description: "Per-traveler tracking, permissions, and sync status.",
      path: "/admin/travelers",
    }),
  component: TravelerDetail,
});

const UNKNOWN = "Unknown";
const NOT_REPORTED = "Not reported yet";

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return NOT_REPORTED;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return UNKNOWN;
  const diff = Date.now() - t;
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function fmtAbs(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  return t.toLocaleString();
}

function permChip(v: boolean | null | undefined): {
  variant: StatusChipVariant;
  label: string;
} {
  if (v === true) return { variant: "success", label: "Granted" };
  if (v === false) return { variant: "danger", label: "Denied" };
  return { variant: "neutral", label: NOT_REPORTED };
}

type Staleness = { variant: StatusChipVariant; label: string; warn: boolean };
function stalenessOf(iso: string | null | undefined): Staleness {
  if (!iso) return { variant: "neutral", label: NOT_REPORTED, warn: true };
  const age = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(age)) return { variant: "neutral", label: UNKNOWN, warn: true };
  if (age < 15 * 60 * 1000) return { variant: "success", label: "Fresh", warn: false };
  if (age < 60 * 60 * 1000) return { variant: "warning", label: "Stale (>15m)", warn: true };
  if (age < 24 * 60 * 60 * 1000) return { variant: "warning", label: "Stale (>1h)", warn: true };
  return { variant: "danger", label: "Stale (>24h)", warn: true };
}

type PluginEntry = { name: string; state: string; probedMethod?: string | null };
function findPlugin(
  inv: PluginEntry[] | undefined,
  match: RegExp,
): PluginEntry | undefined {
  return inv?.find((p) => match.test(p.name));
}
function pluginChip(p: PluginEntry | undefined): {
  variant: StatusChipVariant;
  label: string;
} {
  if (!p) return { variant: "neutral", label: NOT_REPORTED };
  switch (p.state) {
    case "registered_ok":
      return { variant: "success", label: "Available" };
    case "registered_errored":
      return { variant: "warning", label: "Errored" };
    case "not_registered":
      return { variant: "danger", label: "Missing" };
    default:
      return { variant: "neutral", label: UNKNOWN };
  }
}

function TravelerDetail() {
  const { userId } = useParams({ from: "/_authenticated/admin/travelers/$userId" });
  const nav = useNavigate();
  const qc = useQueryClient();

  const tripFn = useServerFn(getMyTrip);
  const membersFn = useServerFn(getTripMembers);
  const diagFn = useServerFn(getTripDiagnostics);
  const statusFn = useServerFn(getTravelerStatus);
  const invitesFn = useServerFn(listInviteCodes);
  const setColorFn = useServerFn(updateTripMemberColor);

  const { data: tripData } = useQuery({
    queryKey: ["my-trip"],
    queryFn: () => tripFn({}),
  });
  const tripId = tripData?.trip?.id ?? null;

  const { data: membersData } = useQuery({
    queryKey: ["trip-members", tripId],
    queryFn: () => membersFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });

  const { data: diagData } = useQuery({
    queryKey: ["trip-diagnostics", tripId],
    queryFn: () => diagFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });

  const {
    data: statusData,
    isLoading: statusLoading,
    error: statusError,
  } = useQuery({
    queryKey: ["traveler-status", tripId, userId],
    queryFn: () => statusFn({ data: { tripId: tripId!, userId } }),
    enabled: !!tripId,
    retry: 1,
  });

  const { data: invitesData } = useQuery({
    queryKey: ["invites", tripId],
    queryFn: () => invitesFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });

  const member = membersData?.members?.find((m) => m.userId === userId);
  const diag = diagData?.byUser?.[userId];
  const status = statusData?.status;

  // Linked invite — best-effort: any unredeemed code with note matching displayName,
  // OR any code whose redeemed_by === userId (so admins can copy what was used).
  const linkedInvite =
    invitesData?.codes?.find(
      (c: any) =>
        c.redeemed_by === userId ||
        (member?.displayName &&
          c.note &&
          c.note.toLowerCase().includes(member.displayName.toLowerCase()) &&
          !c.redeemed_by &&
          !c.revoked_at),
    ) ?? null;

  const [pickerOpen, setPickerOpen] = useState(false);

  const colorMut = useMutation({
    mutationFn: (color: string | null) =>
      setColorFn({ data: { tripId: tripId!, userId, color } }),
    onSuccess: () => {
      toast.success("Color updated");
      qc.invalidateQueries({ queryKey: ["trip-members", tripId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to update color"),
  });

  // Tracking derivation
  let trackingChip: { variant: StatusChipVariant; label: string };
  if (!status?.lastLocationAt) {
    trackingChip = { variant: "neutral", label: UNKNOWN };
  } else {
    const age = Date.now() - new Date(status.lastLocationAt).getTime();
    trackingChip =
      age < 15 * 60 * 1000
        ? { variant: "success", label: "On" }
        : { variant: "warning", label: "Off" };
  }

  const effectiveColor = member?.color ?? colorForUser(userId);
  const colorName = nameForColor(member?.color ?? null);
  const initial = (member?.displayName ?? "?").slice(0, 1).toUpperCase();

  // Invite/member status
  let inviteStatus: { variant: StatusChipVariant; label: string };
  if (!member) inviteStatus = { variant: "neutral", label: UNKNOWN };
  else if (member.submittedAt)
    inviteStatus = { variant: "info", label: "Submitted" };
  else inviteStatus = { variant: "success", label: "Joined" };

  // Background readiness
  let bgReadyChip: { variant: StatusChipVariant; label: string };
  if (!diag) bgReadyChip = { variant: "neutral", label: NOT_REPORTED };
  else if (diag.locationAuthLevel === "always")
    bgReadyChip = { variant: "success", label: "Always-on ready" };
  else if (diag.locationAuthLevel)
    bgReadyChip = { variant: "warning", label: `Needs attention (${diag.locationAuthLevel})` };
  else bgReadyChip = { variant: "neutral", label: UNKNOWN };

  // Plugin presence from device_diagnostics.plugin_inventory
  const inv = diag?.pluginInventory as PluginEntry[] | undefined;
  const pushPlugin = findPlugin(inv, /push/i);
  const liveActPlugin = findPlugin(inv, /live ?activity/i);
  const healthPlugin = findPlugin(inv, /health/i);
  const batteryPlugin = findPlugin(inv, /battery/i);
  const networkPlugin = findPlugin(inv, /network/i);
  const locationBgPlugin = findPlugin(inv, /location.?bg|background.?loc/i);
  const uploadWorkerPlugin = findPlugin(inv, /upload/i);

  // Diagnostics freshness
  const diagFresh = stalenessOf(diag?.updatedAt);
  const permFresh = stalenessOf(diag?.permissions.updatedAt);

  // Platform: prefer self-reported, fall back to inference.
  const platformLabel = diag?.platform
    ? diag.platform
    : diag?.appBuild
      ? "ios (inferred)"
      : diag?.appVersion
        ? UNKNOWN
        : NOT_REPORTED;

  const batteryLabel =
    diag?.batteryLevel == null
      ? NOT_REPORTED
      : `${diag.batteryLevel}%${diag.batteryState ? ` · ${diag.batteryState}` : ""}${diag.lowPowerMode ? " · Low Power" : ""}`;
  const batteryChip: { variant: StatusChipVariant; label: string } | undefined =
    diag?.batteryLevel == null
      ? undefined
      : diag.batteryLevel < 20
        ? { variant: "danger", label: "Low" }
        : diag.batteryLevel < 40
          ? { variant: "warning", label: "Mid" }
          : { variant: "success", label: "OK" };
  const networkChipDerived: { variant: StatusChipVariant; label: string } | undefined =
    diag?.networkType == null
      ? undefined
      : diag.networkType === "wifi"
        ? { variant: "success", label: "Wi-Fi" }
        : diag.networkType === "cellular"
          ? { variant: "info", label: "Cellular" }
          : diag.networkType === "none"
            ? { variant: "danger", label: "Offline" }
            : { variant: "neutral", label: UNKNOWN };
  const pushChip: { variant: StatusChipVariant; label: string } =
    diag?.pushRegistered === true
      ? { variant: "success", label: "Registered" }
      : diag?.pushRegistered === false
        ? { variant: "warning", label: "No token" }
        : { variant: "neutral", label: NOT_REPORTED };
  const shellChip: { variant: StatusChipVariant; label: string } =
    diag?.shellMode === "remote"
      ? { variant: "success", label: "Remote" }
      : diag?.shellMode === "bundled"
        ? { variant: "warning", label: "Bundled" }
        : { variant: "neutral", label: NOT_REPORTED };



  // Readiness — uses full per-user status on the detail page.
  const readiness = evaluateReadiness({
    member: member ? { joinedAt: member.joinedAt, submittedAt: member.submittedAt } : null,
    diagnostics: diag ?? null,
    status: status ?? null,
    lastPingAt: status?.lastLocationAt ?? null,
    hasPings: !!status?.lastLocationAt,
  });
  const readinessVariant = readinessChipVariant(readiness.severity);

  function copyInvite(code: string) {
    navigator.clipboard
      .writeText(code)
      .then(() => toast.success(`Copied ${code}`))
      .catch(() => toast.error("Copy failed"));
  }

  return (
    <main className="mx-auto max-w-3xl px-5 md:px-8 py-6 md:py-8 grid gap-5 overflow-x-hidden">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => nav({ to: "/admin/invites" })}
          className="inline-flex items-center gap-1 text-sm font-medium text-[var(--roava-text-muted)] hover:text-[var(--roava-text)] transition"
        >
          <ChevronLeft className="size-4" />
          Back to Invites
        </button>
      </div>

      <PageHeader
        title="Traveler Status"
        subtitle={member?.displayName ?? "Traveler"}
      />

      {statusError && (
        <RoavaCard>
          <p className="text-sm text-[var(--roava-text-muted)]">
            Status may be stale — couldn't load latest activity for this traveler.
          </p>
        </RoavaCard>
      )}

      {diagFresh.warn && (
        <RoavaCard>
          <div className="flex items-start gap-3">
            <span className="grid place-items-center size-8 rounded-full bg-[var(--roava-orange)]/15 text-[var(--roava-orange)] shrink-0">
              <AlertTriangle className="size-4" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[var(--roava-text)]">
                Device diagnostics may be stale
              </p>
              <p className="text-[12px] text-[var(--roava-text-muted)]">
                Last reported {fmtRelative(diag?.updatedAt)}
                {fmtAbs(diag?.updatedAt) ? ` · ${fmtAbs(diag?.updatedAt)}` : ""}.
                Values below reflect the last self-report from the traveler's device.
              </p>
            </div>
          </div>
        </RoavaCard>
      )}

      {/* 1. Header */}
      <RoavaCard>
        <div className="flex items-center gap-4">
          <Avatar
            className="size-16"
            style={{ boxShadow: `0 0 0 3px ${effectiveColor}` } as React.CSSProperties}
          >
            {member?.avatarUrl && <AvatarImage src={member.avatarUrl} alt="" />}
            <AvatarFallback className="text-lg">{initial}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-[var(--roava-text)] truncate">
              {member?.displayName ?? "Traveler"}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <StatusChip variant="neutral">Member</StatusChip>
              <StatusChip variant={inviteStatus.variant}>{inviteStatus.label}</StatusChip>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3 rounded-2xl border border-[var(--roava-border)] bg-[var(--roava-card)]/50 p-3">
          <span
            className="size-8 rounded-full shrink-0 ring-1 ring-[var(--roava-border)]"
            style={{ backgroundColor: effectiveColor }}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[var(--roava-text)]">{colorName}</p>
            <p className="text-[11px] text-[var(--roava-text-muted)]">
              {member?.color ? "Custom color" : "Auto-assigned"}
            </p>
          </div>
          <SecondaryButton
            size="sm"
            onClick={() => setPickerOpen(true)}
            disabled={!member}
          >
            Edit color
          </SecondaryButton>
        </div>
      </RoavaCard>

      {/* 1.25 Readiness — advisory only */}
      <RoavaCard>
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.12em] text-[var(--roava-text-muted)]">
                Readiness
              </span>
              <StatusChip variant={readinessVariant}>{readiness.status}</StatusChip>
            </div>
            <ul className="mt-2 grid gap-1.5">
              {readiness.reasons.map((r) => (
                <li
                  key={r.code}
                  className="text-sm text-[var(--roava-text)] leading-snug"
                >
                  • {r.label}
                </li>
              ))}
            </ul>
            {readiness.reasons.some((r) => r.suggestedAction) && (
              <div className="mt-3 rounded-xl border border-[var(--roava-border)] bg-[var(--roava-card)]/40 p-3">
                <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--roava-text-muted)] mb-1">
                  Suggested action
                </p>
                <ul className="grid gap-1">
                  {readiness.reasons
                    .filter((r) => r.suggestedAction)
                    .map((r) => (
                      <li
                        key={`act-${r.code}`}
                        className="text-[13px] text-[var(--roava-text)] leading-snug"
                      >
                        {r.suggestedAction}
                      </li>
                    ))}
                </ul>
              </div>
            )}
            <p className="mt-2 text-[11px] text-[var(--roava-text-muted)]">
              Evaluated {new Date(readiness.lastEvaluatedAt).toLocaleString()}
            </p>
          </div>
        </div>
      </RoavaCard>

      {/* 1.5 Device health */}
      <RoavaCard
        title="Device health"
        description="Self-reported from the traveler's device. Updated on app launch and periodically."
      >
        <ul className="grid gap-2">
          <Row
            icon={<Smartphone className="size-4" />}
            label="Platform"
            value={platformLabel}
          />
          <Row
            icon={<Smartphone className="size-4" />}
            label="Device model"
            value={diag?.deviceModel ?? NOT_REPORTED}
          />
          <Row
            icon={<Smartphone className="size-4" />}
            label="OS version"
            value={diag?.osVersion ?? NOT_REPORTED}
          />
          <Row
            icon={<Smartphone className="size-4" />}
            label="App version"
            value={
              diag?.appVersion
                ? `${diag.appVersion}${diag.appBuild ? ` (build ${diag.appBuild})` : ""}`
                : NOT_REPORTED
            }
          />
          <Row
            icon={<Cloud className="size-4" />}
            label="OTA bundle"
            value={diag?.otaBuildId ?? NOT_REPORTED}
          />
          <Row
            icon={<Cloud className="size-4" />}
            label="Shell mode"
            chip={shellChip}
            sub={diag?.lastShellError ? `Last error: ${diag.lastShellError}` : undefined}
          />
          <Row
            icon={<Activity className="size-4" />}
            label="Last diagnostics update"
            value={fmtRelative(diag?.updatedAt)}
            sub={fmtAbs(diag?.updatedAt) ?? undefined}
            chip={{ variant: diagFresh.variant, label: diagFresh.label }}
          />
          <Row
            icon={<MapPin className="size-4" />}
            label="Location permission level"
            value={diag?.locationAuthLevel ?? NOT_REPORTED}
          />
          <Row
            icon={<MapPin className="size-4" />}
            label="Background location readiness"
            chip={bgReadyChip}
          />
          <Row
            icon={<MapPin className="size-4" />}
            label="Background location plugin"
            chip={pluginChip(locationBgPlugin)}
          />
          <Row
            icon={<Cloud className="size-4" />}
            label="Upload worker"
            chip={pluginChip(uploadWorkerPlugin)}
          />
          <Row
            icon={<MapPin className="size-4" />}
            label="Native queue"
            value={
              diag?.nativeQueueCount == null
                ? NOT_REPORTED
                : `${diag.nativeQueueCount} point${diag.nativeQueueCount === 1 ? "" : "s"}`
            }
          />
          <Row
            icon={<MapPin className="size-4" />}
            label="Last native location"
            value={fmtRelative(diag?.lastNativeLocationAt)}
            sub={fmtAbs(diag?.lastNativeLocationAt) ?? undefined}
          />
          <Row
            icon={<Bell className="size-4" />}
            label="Notifications permission"
            chip={permChip(diag?.permissions.notificationsGranted)}
          />
          <Row
            icon={<Bell className="size-4" />}
            label="Push token"
            chip={pushChip}
            sub={pushPlugin ? `Plugin: ${pushPlugin.state}` : undefined}
          />
          <Row
            icon={<CameraIcon className="size-4" />}
            label="Photos permission"
            chip={permChip(diag?.permissions.photosGranted)}
          />
          <Row
            icon={<Radio className="size-4" />}
            label="Live Activity"
            chip={pluginChip(liveActPlugin)}
            sub={diag?.liveActivityState ?? undefined}
          />
          <Row
            icon={<HeartPulse className="size-4" />}
            label="HealthKit"
            chip={pluginChip(healthPlugin)}
            sub={diag?.healthkitState ?? undefined}
          />
          <Row
            icon={<Battery className="size-4" />}
            label="Battery"
            value={batteryLabel}
            chip={batteryChip}
            sub={batteryPlugin ? `Plugin: ${batteryPlugin.state}` : undefined}
          />
          <Row
            icon={<Wifi className="size-4" />}
            label="Network"
            value={diag?.networkType ?? NOT_REPORTED}
            chip={networkChipDerived}
            sub={networkPlugin ? `Plugin: ${networkPlugin.state}` : undefined}
          />
          <Row
            icon={<Zap className="size-4" />}
            label="Permissions report"
            value={fmtRelative(diag?.permissions.updatedAt)}
            sub={fmtAbs(diag?.permissions.updatedAt) ?? undefined}
            chip={{ variant: permFresh.variant, label: permFresh.label }}
          />
        </ul>
      </RoavaCard>

      {/* 2. Tracking */}
      <RoavaCard title="Tracking" description="Recent activity from this traveler's device.">
        <ul className="grid gap-2">
          <Row icon={<Activity className="size-4" />} label="Tracking" chip={trackingChip} />
          <Row
            icon={<MapPin className="size-4" />}
            label="Last route ping"
            value={statusLoading ? "Loading…" : fmtRelative(status?.lastLocationAt)}
            sub={fmtAbs(status?.lastLocationAt) ?? undefined}
          />
          <Row
            icon={<MapPin className="size-4" />}
            label="Last GPS fix"
            value={statusLoading ? "Loading…" : fmtRelative(status?.lastLocationAt)}
          />
          <Row
            icon={<Activity className="size-4" />}
            label="Last app activity"
            value={fmtRelative(diag?.updatedAt)}
            sub={fmtAbs(diag?.updatedAt) ?? undefined}
          />
          <Row
            icon={<Cloud className="size-4" />}
            label="Last sync"
            value={statusLoading ? "Loading…" : fmtRelative(status?.lastMediaUploadedAt)}
          />
          <Row
            icon={<Wifi className="size-4" />}
            label="Network status"
            value={diag?.networkType ?? NOT_REPORTED}
            chip={networkChipDerived}
          />
          <Row
            icon={<Battery className="size-4" />}
            label="Battery level"
            value={batteryLabel}
            chip={batteryChip}
          />
        </ul>
      </RoavaCard>

      {/* 3. Permissions */}
      <RoavaCard title="Permissions" description="Last self-reported from the traveler's device.">
        <ul className="grid gap-2">
          <Row
            icon={<MapPin className="size-4" />}
            label="Location"
            chip={permChip(diag?.permissions.locationGranted)}
          />
          <Row
            icon={<MapPin className="size-4" />}
            label="Background readiness"
            chip={bgReadyChip}
          />
          <Row
            icon={<CameraIcon className="size-4" />}
            label="Photos / camera"
            chip={permChip(diag?.permissions.photosGranted)}
          />
          <Row
            icon={<Bell className="size-4" />}
            label="Notifications"
            chip={permChip(diag?.permissions.notificationsGranted)}
          />
          <Row
            icon={<Activity className="size-4" />}
            label="Motion / fitness"
            value={NOT_REPORTED}
          />
        </ul>
      </RoavaCard>

      {/* 4. Upload / Sync */}
      <RoavaCard title="Upload & sync">
        <ul className="grid gap-2">
          <Row
            icon={<MapPin className="size-4" />}
            label="Pending route points"
            value={
              diag?.nativeQueueCount == null
                ? NOT_REPORTED
                : String(diag.nativeQueueCount)
            }
          />
          <Row
            icon={<Cloud className="size-4" />}
            label="Pending media"
            value={statusLoading ? "Loading…" : String(status?.pendingMediaCount ?? 0)}
          />
          <Row
            icon={<Cloud className="size-4" />}
            label="Failed media"
            value={statusLoading ? "Loading…" : String(status?.failedMediaCount ?? 0)}
          />
          <Row
            icon={<CameraIcon className="size-4" />}
            label="Uploaded photos"
            value={statusLoading ? "Loading…" : String(status?.uploadedPhotos ?? 0)}
          />
          <Row
            icon={<CameraIcon className="size-4" />}
            label="Uploaded videos"
            value={statusLoading ? "Loading…" : String(status?.uploadedVideos ?? 0)}
          />
          <Row
            icon={<Cloud className="size-4" />}
            label="Last successful sync"
            value={statusLoading ? "Loading…" : fmtRelative(status?.lastMediaUploadedAt)}
            sub={fmtAbs(status?.lastMediaUploadedAt) ?? undefined}
          />
          <Row
            icon={<CameraIcon className="size-4" />}
            label="Last media captured"
            value={statusLoading ? "Loading…" : fmtRelative(status?.lastMediaCapturedAt)}
            sub={fmtAbs(status?.lastMediaCapturedAt) ?? undefined}
          />
        </ul>
      </RoavaCard>

      {/* 5. Admin actions */}
      <RoavaCard title="Admin actions">
        <div className="flex flex-wrap items-center gap-2">
          <SecondaryButton size="sm" onClick={() => setPickerOpen(true)} disabled={!member}>
            Edit color
          </SecondaryButton>
          {linkedInvite && (
            <SecondaryButton size="sm" onClick={() => copyInvite(linkedInvite.code)}>
              <Copy className="size-4 mr-1.5" />
              Copy invite code
            </SecondaryButton>
          )}
          <Link
            to="/admin/invites"
            className="ml-auto text-xs text-[var(--roava-text-muted)] hover:text-[var(--roava-text)]"
          >
            All invites
          </Link>
        </div>
      </RoavaCard>

      <TravelerColorPicker
        member={member ?? null}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSave={(color) => colorMut.mutate(color)}
        onReset={() => colorMut.mutate(null)}
        conflictNameByColor={Object.fromEntries(
          (membersData?.members ?? [])
            .filter((mm) => mm.userId !== userId)
            .map((mm) => [
              (mm.color ?? colorForUser(mm.userId)).toLowerCase(),
              mm.displayName,
            ]),
        )}
      />
    </main>
  );
}

function Row({
  icon,
  label,
  value,
  sub,
  chip,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
  sub?: string;
  chip?: { variant: StatusChipVariant; label: string };
}) {
  return (
    <li className="flex items-center gap-3 rounded-2xl border border-[var(--roava-border)] bg-[var(--roava-card)]/40 px-3 py-3">
      <span className="grid place-items-center size-8 rounded-full bg-[var(--roava-gray)] text-[var(--roava-text-muted)] shrink-0">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--roava-text)] truncate">{label}</p>
        {sub && (
          <p className="text-[11px] text-[var(--roava-text-muted)] truncate">{sub}</p>
        )}
      </div>
      {chip ? (
        <StatusChip variant={chip.variant}>{chip.label}</StatusChip>
      ) : (
        <span className="text-sm text-[var(--roava-text-muted)] shrink-0">{value}</span>
      )}
    </li>
  );
}
